/* eslint-disable max-len, react/prop-types */
import React, {
  useMemo, useState, useEffect, forwardRef
} from 'react';
import * as Yup from 'yup';
import { Form, Clamp } from 'components/atoms'
import { useFormikForwardRef } from 'hooks'
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldCustom, FieldSelect, FieldDate, FieldTimePicker
} from 'components/molecules';
import { linkRegex } from 'services/constants'
import { convertSessionPropsToFormProps, convertSessionFormToProps } from 'services/generators'
import { useDebounce } from 'react-use'
import { getCourseInfo } from 'services/requests'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify';
import { Toast } from 'containers';

const computeCourseOptions = (opts) => opts.reduce((acc, degree) => [...acc, ...degree.list.map(course => ({ label: course.name, value: course.id, original: course }))], [])

const SessionFormSchema = Yup.object().shape({
  id: Yup.number()
    .required('Campo obrigatório'),
  registrationLink: Yup.string().matches(
    linkRegex,
    'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.'
  )
    .required('Campo obrigatório'),
  date: Yup.string().required('Campo obrigatório'),
  startHour: Yup.string().nullable().required('Campo obrigatório'),
  endHour: Yup.string().nullable().required('Campo obrigatório')
    .test(
      'is-after-start',
      'Hora de fim tem de ser depois da hora de inicio.',
      function testHour(value) {
        const { parent: { startHour } } = this
        if (!value) return false
        const startHourNumber = Number(startHour.split(':').join(''))
        const endHourNumber = Number(value.split(':').join(''))
        return startHourNumber < endHourNumber
      }),
});

const XperimentaSchema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  sessions: Yup.array().of(Yup.object().shape({
    id: Yup.number()
      .required('Campo obrigatório'),
    registrationLink: Yup.string().matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
      .required('Campo obrigatório'),
    startDate: Yup.string().nullable().required('Campo obrigatório'),
    endDate: Yup.string().nullable().required('Campo obrigatório'),
  }))
    .min(1, 'Mínimo de 1 sessão')
    .required('Campo obrigatório'),
});

// eslint-disable-next-line react/prop-types
const SessionItemForm = forwardRef(({
  initialValues, onSubmit, courses, sessions
}, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(
    () => convertSessionPropsToFormProps(initialValues),
    [initialValues]
  )

  // remove courses that already have sessions and keep selected
  const coursesOptionsClean = useMemo(() => {
    const coursesWithoutSessions = courses.filter((course) => !sessions.find(s => s.id === course.value))
    const selectedCourse = courses.find(c => c.value === initialValues.id)
    if (selectedCourse) return [selectedCourse, ...coursesWithoutSessions]
    return coursesWithoutSessions
  }, [courses, sessions, initialValues])

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={SessionFormSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(convertSessionFormToProps(values, courses))
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Curso"
          name="id"
          component={FieldSelect}
          placeholder="Selecione o curso..."
          options={coursesOptionsClean}
          info="Selecione o curso, só é possível selecionar cursos sem sessões associadas."
        />
        <Field
          title="Link"
          name="registrationLink"
          placeholder="Link de sessão"
          component={FieldCustom}
          info="Link da sessão de zoom"
        />
        <Field
          title="Dia da sessão"
          name="date"
          placeholder="Data da sessão"
          component={FieldDate}
        />
        <Field
          title="Hora de inicio"
          name="startHour"
          placeholder="Hora de inicio da sessão"
          component={FieldTimePicker}
        />
        <Field
          title="Hora de fim"
          name="endHour"
          placeholder="Hora de fim da sessão"
          component={FieldTimePicker}
          info="hora prevista do final da sessão"
        />
      </Form>
    </Formik>
  )
})

const CellContent = ({ label }) => (
  <Clamp>
    {label}
  </Clamp>
)

const XperimentaEditor = ({ initialProps, onChange }) => {
  const { lang } = useParams()
  const [courses, setCourses] = useState([]);
  const [isLoading, setLoading] = useState(true)
  const [editorProps, setEditorProps] = useState(initialProps)

  useEffect(() => {
    const getCoursesData = async () => {
      try {
        setLoading(true)
        const { data: { data: coursesData } } = await getCourseInfo({ lang });

        setCourses(computeCourseOptions(coursesData.list))

        setLoading(false)
      } catch (err) {
        setLoading(false)
        toast.error(
          <Toast
            type="error"
            title="Erro a carregar formulário de Xperimenta"
            description={err.message}
          />
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
    getCoursesData();
  }, [lang]);

  const initialValues = useMemo(() => {
    const {
      title, sessions
    } = initialProps

    if (isLoading) {
      return {
        title,
        sessions
      }
    }
    return {
      title,
      sessions: sessions.map(sesssionData => {
        const { label } = (courses.find(c => c.value === sesssionData.id) || { label: '' })
        return {
          ...sesssionData,
          label
        }
      })
    }
  }, [initialProps, courses, isLoading])

  const handleChange = (newValues) => {
    const newProps = { ...initialProps, ...newValues };
    const isValid = XperimentaSchema.isValidSync(newValues)
    if (isValid) setEditorProps(newProps)
  }

  useDebounce(() => {
    // remove label key before triggering onChange
    // eslint-disable-next-line no-unused-vars
    const sessionsClean = editorProps.sessions.map(({ label, ...session }) => session)
    const editorPropsClean = { ...editorProps, sessions: sessionsClean }
    onChange(editorPropsClean);
  }, 500,
  [editorProps]
  )
  if (isLoading) {
    return (
      <div>
        A carregar...
      </div>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={XperimentaSchema}
      validate={handleChange}
    >
      <Form>
        <Field
          title="Sessões"
          name="sessions"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'id',
              Header: 'Curso',
              Cell: ({ original }) => <CellContent label={original.label} />
            },
          ]}
          rowKey="id"
          ItemForm={SessionItemForm}
          newItemText="Nova sessão"
          itemFormProps={{ courses, sessions: initialProps.sessions }}
          shouldHideHeaders
          shouldAddSearch
          searchKeys={['label', 'id']}
          shouldAllowReorder={false}
          searchPlaceholder="pesquisar sessões"
        />
      </Form>
    </Formik>
  );
}

export default XperimentaEditor;
